import React, { useState, useRef, useEffect } from "react";
import { t } from "../i18n";
import { Dialog } from "./Dialog";
import { TextField } from "./TextField";
import { FilledButton } from "./FilledButton";
import clsx from "clsx";
import { ArrowIcon, clipboard, RedoIcon, ReloadIcon, DiscordIcon, LinkedinIcon, EmailIcon, WebsiteIcon } from "./icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  generateChatResponse,
  validateAIConfig,
  fetchAvailableModels,
} from "../utils/aiClient";
import { useExcalidrawElements, useExcalidrawAppState, useExcalidrawSetAppState } from "./App";
import { serializeAsJSON, getMinimalElementsForAI, getMinimalAppStateForAI } from "../data/json";
import { getSelectedElements, getNonDeletedElements } from "@excalidraw/element";
import { BinaryFiles, ChatMessage } from "../types";

import "./Threatcalidraw.scss";

type Page = "analyze" | "configure" | "about";
type ModelProvider = "google" | "openai" | "anthropic" | "openrouter";

export const Threatcalidraw = ({ onClose }: { onClose: () => void }) => {
  const [activePage, setActivePage] = useState<Page>("analyze");
  const [apiKey, setApiKey] = useState(
    localStorage.getItem("excalidraw-threat-modeller-api-key") || "",
  );
  const [modelId, setModelId] = useState(
    localStorage.getItem("excalidraw-threat-modeller-model-id") || "",
  );
  const [modelProvider, setModelProvider] = useState<ModelProvider>(
    (localStorage.getItem("excalidraw-threat-modeller-provider") as ModelProvider) || "google",
  );
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectionToggled, setIsSelectionToggled] = useState(false);
  const [isIncludeDiagramToggled, setIsIncludeDiagramToggled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelSearch, setModelSearch] = useState(
    localStorage.getItem("excalidraw-threat-modeller-model-id") || "",
  );
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showExampleQuestions, setShowExampleQuestions] = useState(false);
  const [showProviderSuggestions, setShowProviderSuggestions] = useState(false);
  const [providerSearch, setProviderSearch] = useState<string>(modelProvider);
  const [messageContextLimit, setMessageContextLimit] = useState(
    parseInt(localStorage.getItem("excalidraw-threat-modeller-message-history-limit") || "50", 10),
  );
  const comboboxRef = useRef<HTMLDivElement>(null);
  const providerComboboxRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const excalidrawElements = useExcalidrawElements();
  const excalidrawAppState = useExcalidrawAppState();
  const setAppState = useExcalidrawSetAppState();
  const messages = excalidrawAppState.threatcalidrawMessages || [];

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000); // Auto-dismiss after 3 seconds
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000); // Auto-dismiss after 3 seconds
  };

  const scrollToBottom = (smooth = true) => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    
    if (smooth) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollButton(!isAtBottom && messages.length > 0);
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom(false);
    }
  }, [messages]);

  // Persist chat history to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("excalidraw-threat-modeller-chat-history", JSON.stringify(messages));
    }
  }, [messages]);

  // Load chat history from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem("excalidraw-threat-modeller-chat-history");
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        setAppState((state) => ({
          threatcalidrawMessages: parsedMessages,
        }));
      } catch (err) {
        console.error("Failed to load chat history:", err);
      }
    }
  }, []);

  // Fetch available models when Configure page is opened
  useEffect(() => {
    if (activePage === "configure" && modelProvider && apiKey) {
      // Don't automatically fetch models - user can manually refresh if needed
      // fetchModels() is now only called when save is clicked
    }
  }, [activePage, modelProvider, apiKey]);

  // Handle click outside suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
        setShowModelSuggestions(false);
      }
      if (providerComboboxRef.current && !providerComboboxRef.current.contains(event.target as Node)) {
        setShowProviderSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      setError(null);
      const models = await fetchAvailableModels(modelProvider, apiKey);
      setAvailableModels(models);
    } catch (err) {
      showError(`Failed to fetch models: ${err instanceof Error ? err.message : String(err)}`);
      setAvailableModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const deleteChatHistory = () => {
    if (confirm("Are you sure you want to delete all chat history? This action cannot be undone.")) {
      localStorage.removeItem("excalidraw-threat-modeller-chat-history");
      setAppState((state) => ({
        threatcalidrawMessages: [],
      }));
      showSuccess("Chat history deleted successfully");
    }
  };

  const exportChatHistory = () => {
    if (messages.length === 0) {
      showError("No chat history to export");
      return;
    }

    // Format chat history as markdown
    let chatContent = "# threatCalidraw Chat History\n\n";
    chatContent += `**Exported on:** ${new Date().toLocaleString()}\n`;
    chatContent += `**Model Provider:** ${modelProvider.charAt(0).toUpperCase() + modelProvider.slice(1)}\n`;
    chatContent += `**Model ID:** ${modelId}\n\n`;
    chatContent += "---\n\n";

    messages.forEach((message) => {
      if (message.content.length > 0) {
        if (message.role === "user") {
          chatContent += "## User\n\n";
        } else {
          chatContent += "## AI Assistant\n\n";
        }
        chatContent += `${message.content}\n\n`;
        chatContent += "---\n\n";
      }
    });

    // Create blob and download
    const blob = new Blob([chatContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `threat-modeling-chat-${Date.now()}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showSuccess("Chat history exported successfully");
  };

  const saveSettings = async () => {
    const validation = validateAIConfig(modelProvider, modelId, apiKey);
    if (!validation.valid) {
      showError(validation.error || "Invalid configuration");
      setSuccess(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      localStorage.setItem("excalidraw-threat-modeller-api-key", apiKey);
      localStorage.setItem("excalidraw-threat-modeller-model-id", modelId);
      localStorage.setItem("excalidraw-threat-modeller-provider", modelProvider);
      localStorage.setItem("excalidraw-threat-modeller-message-history-limit", messageContextLimit.toString());
      
      showSuccess("✓ Settings saved successfully!");
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      showError(`Failed to save settings: ${errorMessage}`);
      setSuccess(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) {
      return;
    }

    // Validate configuration
    const validation = validateAIConfig(modelProvider, modelId, apiKey);
    if (!validation.valid) {
      showError(validation.error || "Invalid configuration");
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue,
    };

    setAppState((state) => ({
      threatcalidrawMessages: [...(state.threatcalidrawMessages || []), userMessage],
    })); // Display only the user's actual message
    setInputValue("");
    setIsLoading(true);
    setError(null);

    try {
      let serializedCanvas = "";
      
      if (isIncludeDiagramToggled) {
        let elementsToSerialize = excalidrawElements;

        if (isSelectionToggled) {
          // Filter for only selected elements
          const selectedElements = getSelectedElements(excalidrawElements, excalidrawAppState);
          if (selectedElements.length > 0) {
            elementsToSerialize = selectedElements;
          } else {
            // If no elements are selected, but toggle is ON, send all non-deleted elements
            elementsToSerialize = getNonDeletedElements(excalidrawElements);
          }
        } else {
          // If toggle is OFF, send all non-deleted elements
          elementsToSerialize = getNonDeletedElements(excalidrawElements);
        }

        // Optimize JSON for AI: remove styling, versioning, and metadata (~60-70% token reduction)
        const minimalElements = getMinimalElementsForAI(elementsToSerialize);
        const minimalAppState = getMinimalAppStateForAI(excalidrawAppState);

        const optimizedData = {
          type: "excalidraw",
          elements: minimalElements,
          appState: minimalAppState,
        };

        serializedCanvas = JSON.stringify(optimizedData, null, 2);
      }

      const canvasContextMessage: ChatMessage = {
        id: (Date.now() + 0.5).toString(), // Ensure it's ordered before the user message
        role: "user",
        content: `The current threatCalidraw(Excalidraw) ${isSelectionToggled ? "selected elements" : "canvas"} context is:\n\`\`\`json\n${serializedCanvas}\n\`\`\``,
      };

      // Build messages array: last (n) interactions + canvas context + current user message
      // Keep only messages with content to avoid incomplete assistant responses
      const completedMessages = messages.filter((msg) => msg.content.length > 0);
      const previousMessages = completedMessages.slice(-messageContextLimit);
      const messagesToSend = serializedCanvas.length > 20 
        ? [...previousMessages, canvasContextMessage, userMessage] 
        : [...previousMessages, userMessage];

      // Create assistant message placeholder
      const assistantMessageId = (Date.now() + 1).toString();
      let assistantContent = "";

      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: assistantContent,
      };

      setAppState((state) => ({
        threatcalidrawMessages: [...(state.threatcalidrawMessages || []), assistantMessage],
      }));

      // Get streaming response
      const stream = generateChatResponse(
        messagesToSend,
        modelProvider,
        modelId,
        apiKey,
        "threatCalidraw",
        `
        **Situation**
        You are 'threatCalidraw' !!  You are a security threat modeling expert assisting users in identifying and mitigating security risks in their system architecture. Users will provide system design diagrams (Excalidraw format), which you excel at parsing and analyzing to identify threats and develop countermeasures using industry-standard threat modeling frameworks. You have demonstrated expertise in extracting system components, data flows, trust boundaries, and external entities from these diagrams. 

        [!Important] Do not talk about excalidraw in your responses and how you are able to understand the diagram using JSON export. This is our secret sauce in the app.

        **Task**
        The assistant should:
        1. Analyze the provided system diagram to identify security threats, vulnerabilities, and potential attack vectors
        2. Engage the user through strategic questioning to understand their specific security concerns, business context, risk priorities, and architectural constraints before and during the threat modeling process
        3. Perform a comprehensive threat modeling analysis using the most appropriate framework (STRIDE for technical threats, PASTA for business-risk focus, or MAESTRO for comprehensive analysis) based on the user's architecture and responses
        4. Present findings in clear, plain language focused on architectural components and security implications
        5. Translate technical architecture into actionable security insights without referencing internal analysis methods

        **Objective**
        Help users systematically identify, understand, and mitigate security risks in their system architecture through collaborative threat modeling conversations that address their entire system, provide clear accessible guidance, and ensure the analysis aligns with their specific business needs and security priorities.

        **Knowledge**
        You have expertise in multiple threat modeling frameworks:
        - **STRIDE**: Spoofing, Tampering, Repudiation, Information Disclosure, Denial of Service, Elevation of Privilege
        - **PASTA**: Process for Attack Simulation and Threat Analysis, emphasizing business context and risk prioritization
        - **MAESTRO**: Modern framework combining multiple analytical perspectives for comprehensive threat assessment

        When analyzing system diagrams, leverage your capability to parse diagram structures and identify: system components, data flows, trust boundaries, external entities, and architectural relationships. Ask clarifying questions about the user's threat landscape, compliance requirements, critical assets, and risk tolerance to recommend the threat modeling framework that best fits their architecture and priorities. Prioritize comprehensive analysis that covers technical, business, and systemic perspectives while remaining responsive to user guidance throughout the process.
        `,
      );

      // Process stream
      for await (const chunk of stream) {
        assistantContent += chunk;
        setAppState((state) => ({
          threatcalidrawMessages: (state.threatcalidrawMessages || []).map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: assistantContent }
              : msg,
          ),
        }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      showError(`${errorMessage}`);
      // Remove the incomplete assistant message
      setAppState((state) => ({
        threatcalidrawMessages: (state.threatcalidrawMessages || []).filter(
          (msg) => msg.role !== "assistant" || msg.content.length > 0,
        ),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Maybe show a small "Copied!" confirmation tooltip in the future
    } catch (err) {
      console.error("Failed to copy text: ", err);
      showError("Failed to copy to clipboard.");
    }
  };

  const handleRetry = async () => {
    const lastUserMessage = messages.slice().reverse().find((msg) => msg.role === "user");
    if (!lastUserMessage) {
      showError("No message to retry.");
      return;
    }

    // Temporarily set the input value to the last message to re-send
    setInputValue(lastUserMessage.content);

    // Remove the last user and assistant messages before retrying
    setAppState((state) => {
      const updatedMessages = (state.threatcalidrawMessages || []).slice();
      let assistantRemoved = false;
      let userRemoved = false;
      
      // Remove from the end: last assistant message, then last user message
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (!assistantRemoved && updatedMessages[i].role === "assistant") {
          updatedMessages.splice(i, 1);
          assistantRemoved = true;
        } else if (assistantRemoved && !userRemoved && updatedMessages[i].role === "user") {
          updatedMessages.splice(i, 1);
          userRemoved = true;
          break;
        }
      }
      return { threatcalidrawMessages: updatedMessages };
    });

    // Set a timeout to allow state to update before re-sending
    setTimeout(() => {
      handleSendMessage();
    }, 0);
  };

  const renderSidebar = () => (
    <div className="threatcalidraw__sidebar">
      <div className="threatcalidraw__sidebar-items">
        <button
          className={clsx("threatcalidraw__sidebar-item", {
            "threatcalidraw__sidebar-item--active": activePage === "analyze",
          })}
          onClick={() => setActivePage("analyze")}
        >
          <span className="threatcalidraw__sidebar-item-label">Analyze</span>
        </button>
        <button
          className={clsx("threatcalidraw__sidebar-item", {
            "threatcalidraw__sidebar-item--active": activePage === "configure",
          })}
          onClick={() => setActivePage("configure")}
        >
          <span className="threatcalidraw__sidebar-item-label">Settings</span>
        </button>
        <button
          className={clsx("threatcalidraw__sidebar-item", {
            "threatcalidraw__sidebar-item--active": activePage === "about",
          })}
          onClick={() => setActivePage("about")}
        >
          <span className="threatcalidraw__sidebar-item-label">About</span>
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activePage) {
      case "analyze":
        return (
          <div className="threatcalidraw__page threatcalidraw__page--chat" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {error && (
              <div className="threatcalidraw__error-banner">
                <span>{error}</span>
                <button
                  className="threatcalidraw__error-close"
                  onClick={() => setError(null)}
                >
                  ×
                </button>
              </div>
            )}
            <div 
              className="threatcalidraw__messages" 
              style={{ flex: 1, overflowY: "auto", position: "relative" }}
              ref={messagesContainerRef}
              onScroll={handleScroll}
            >
              {messages.length === 0 && !isLoading ? (
                <div className="threatcalidraw__placeholder">
                  <h4> AI-Powered Threat modeling</h4>
                  <p>
                    Welcome to threatCalidraw! Start by asking questions about your diagram's security.
                  </p>
                  <div className="threatcalidraw__placeholder-suggestions">
                    <button
                      className="threatcalidraw__placeholder-toggle"
                      onClick={() => setShowExampleQuestions(!showExampleQuestions)}
                    >
                      <strong>Commonly asked questions ✨</strong>
                      <span className="threatcalidraw__placeholder-toggle-icon">
                        {showExampleQuestions ? "" : ""}
                      </span>
                    </button>
                    {showExampleQuestions && (
                      <ul>
                        <li onClick={() => setInputValue("Summarize the architecture, list down the data flows and components")}>Summarize the architecture, list down the data flows and components</li>
                        <li onClick={() => setInputValue("Generate a threat model report using STRIDE framework")}>Generate a threat model report using STRIDE framework</li>
                        <li onClick={() => setInputValue("Do you have any questions for the developer?")}> Do you have any questions for the developer?</li>
                        {/* <li onClick={() => setInputValue("Analyze for common vulnerabilities based on STRIDE")}>Analyze for common vulnerabilities based on STRIDE</li> */}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={clsx("threatcalidraw__message", {
                      "threatcalidraw__message--user": message.role === "user",
                      "threatcalidraw__message--assistant":
                        message.role === "assistant",
                    })}
                  >
                    <div className="threatcalidraw__message-content">
                      {message.role === "assistant" ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      ) : (
                        message.content
                      )}
                    </div>
                    {message.role === "assistant" && message.content && (
                      <div className="threatcalidraw__message-actions">
                        <button
                          className="threatcalidraw__action-button"
                          onClick={() => handleCopy(message.content)}
                          title="Copy"
                        >
                          {clipboard}
                        </button>
                        {/* Show retry only for the last assistant message */}
                        {index === messages.length - 1 && !isLoading && (
                          <button
                            className="threatcalidraw__action-button"
                            onClick={handleRetry}
                            title="Retry"
                          >
                            {RedoIcon}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
              {isLoading && (
                <div className="threatcalidraw__message threatcalidraw__message--assistant">
                  <div className="threatcalidraw__message-content">
                    <span className="threatcalidraw__loading">●●●</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {showScrollButton && (
              <button
                className="threatcalidraw__scroll-to-bottom"
                onClick={() => scrollToBottom(true)}
                title="Scroll to bottom"
              >
                ↓
              </button>
            )}
            <div className="threatcalidraw__input-area" style={{ flexShrink: 0 }}>
              <div className="threatcalidraw__input-wrapper">
                <textarea
                  className="threatcalidraw__prompt-input"
                  placeholder="Ask AI about your threat model (Shift+Enter for new line)"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isLoading}
                  rows={3}
                  wrap="soft"
                  style={{ 
                    resize: "vertical", 
                    minHeight: "60px", 
                    overflowX: "hidden",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word"
                  }}
                />
                <button
                  className="threatcalidraw__send-button"
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  title="Send"
                >
                  {ArrowIcon}
                </button>
              </div>
              <div className="threatcalidraw__toggle-wrapper">
                <div className="threatcalidraw__toggle-item">
                  <span className="threatcalidraw__toggle-label">Include Diagram</span>
                  <button
                    className={clsx("threatcalidraw__toggle-switch", {
                      "threatcalidraw__toggle-switch--active": isIncludeDiagramToggled,
                    })}
                    onClick={() => setIsIncludeDiagramToggled(!isIncludeDiagramToggled)}
                    disabled={isLoading}
                    title="Include diagram in analysis"
                  >
                    <span className="threatcalidraw__toggle-circle"></span>
                  </button>
                </div>
                <div className="threatcalidraw__toggle-item">
                  <span className="threatcalidraw__toggle-label">Only Selected</span>
                  <button
                    className={clsx("threatcalidraw__toggle-switch", {
                      "threatcalidraw__toggle-switch--active": isSelectionToggled,
                    })}
                    onClick={() => setIsSelectionToggled(!isSelectionToggled)}
                    disabled={isLoading || !isIncludeDiagramToggled}
                    title="Analyze only selected elements"
                  >
                    <span className="threatcalidraw__toggle-circle"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case "configure":
        return (
          <div className="threatcalidraw__page threatcalidraw__page--configure">
            <div className="threatcalidraw__configure-content">
              <h3>Configure</h3>
              {success && (
                <div className="threatcalidraw__success-banner">
                  <span>{success}</span>
                  <button
                    className="threatcalidraw__success-close"
                    onClick={() => setSuccess(null)}
                  >
                    ×
                  </button>
                </div>
              )}
              {error && (
                <div className="threatcalidraw__error-banner">
                  <span>{error}</span>
                  <button
                    className="threatcalidraw__error-close"
                    onClick={() => setError(null)}
                  >
                    ×
                  </button>
                </div>
              )}
            <div className="threatcalidraw__fields">
              <div className="threatcalidraw__field">
                <TextField
                  label="API Key"
                  value={apiKey}
                  onChange={(value) => setApiKey(value)}
                  placeholder="Enter your API key"
                  isRedacted
                />
              </div>
              <div className="threatcalidraw__field-row">
                <div className="threatcalidraw__field">
                  <label>Model Provider</label>
                  <div className="threatcalidraw__combobox" ref={providerComboboxRef}>
                    <input
                      type="text"
                      className="threatcalidraw__combobox-input"
                      value={providerSearch}
                      onChange={(e) => {
                        setProviderSearch(e.target.value);
                        setShowProviderSuggestions(true);
                      }}
                      onFocus={() => setShowProviderSuggestions(true)}
                      placeholder="Search or select provider"
                    />
                    {showProviderSuggestions && (
                      <ul className="threatcalidraw__combobox-list">
                        <li
                          className="threatcalidraw__combobox-item threatcalidraw__combobox-item--clear"
                          onClick={() => {
                            setModelProvider("google");
                            setProviderSearch("");
                            setShowProviderSuggestions(false);
                          }}
                        >
                          Clear selection
                        </li>
                        {(
                          [
                            // { id: "openai", name: "OpenAI" },
                            { id: "google", name: "Google" },
                            { id: "openrouter", name: "OpenRouter" },
                            // { id: "anthropic", name: "Anthropic" },
                          ] as const
                        )
                          .filter(
                            (p) =>
                              p.name.toLowerCase().includes(providerSearch.toLowerCase()) ||
                              p.id.toLowerCase().includes(providerSearch.toLowerCase()),
                          )
                          .map((provider) => (
                            <li
                              key={provider.id}
                              className="threatcalidraw__combobox-item"
                              onClick={() => {
                                setModelProvider(provider.id);
                                setProviderSearch(provider.name);
                                setShowProviderSuggestions(false);
                              }}
                            >
                              <span className="threatcalidraw__combobox-item-name">
                                {provider.name}
                              </span>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="threatcalidraw__field">
                  <label>
                    Model ID
                    {loadingModels && <span> (Loading...)</span>}
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                    <div className="threatcalidraw__combobox" ref={comboboxRef} style={{ flex: 1 }}>
                      <input
                        type="text"
                        className="threatcalidraw__combobox-input"
                        value={modelSearch}
                        onChange={(e) => {
                          setModelSearch(e.target.value);
                          setModelId(e.target.value);
                          setShowModelSuggestions(true);
                        }}
                        onFocus={() => setShowModelSuggestions(true)}
                        placeholder="Search or enter model ID"
                      />
                      {showModelSuggestions && availableModels.length > 0 && (
                        <ul className="threatcalidraw__combobox-list">
                          <li
                            className="threatcalidraw__combobox-item threatcalidraw__combobox-item--clear"
                            onClick={() => {
                              setModelId("");
                              setModelSearch("");
                              setShowModelSuggestions(false);
                            }}
                          >
                            Clear selection
                          </li>
                          {availableModels
                            .filter(
                              (m) =>
                                m.name.toLowerCase().includes(modelSearch.toLowerCase()) ||
                                m.id.toLowerCase().includes(modelSearch.toLowerCase()),
                            )
                            .map((model) => (
                              <li
                                key={model.id}
                                className="threatcalidraw__combobox-item"
                                onClick={() => {
                                  setModelId(model.id);
                                  setModelSearch(model.name);
                                  setShowModelSuggestions(false);
                                }}
                              >
                                <span className="threatcalidraw__combobox-item-name">
                                  {model.name}
                                </span>
                                <span className="threatcalidraw__combobox-item-id">{model.id}</span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                    <button
                      className="threatcalidraw__refresh-button"
                      onClick={fetchModels}
                      disabled={loadingModels}
                      title="Refresh models"
                      type="button"
                    >
                      {ReloadIcon}
                    </button>
                  </div>
                </div>
                <div className="threatcalidraw__field">
                  <label>Context Limit</label>
                  <input
                    type="number"
                    className="threatcalidraw__combobox-input"
                    value={messageContextLimit}
                    onChange={(e) => setMessageContextLimit(parseInt(e.target.value, 10))}
                    placeholder="Maximum number of messages"
                    min="5"
                  />
                </div>
              </div>
            </div>
            <hr style={{ borderColor: "rgba(var(--color-primary-rgb), 0.1)" }} />
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ marginTop: "0", marginBottom: "1rem" }}>Chat Settings</h4>
              <p style={{ fontSize: "0.875rem", color: "var(--icon-fill-color)", opacity: 0.7, marginBottom: "1rem" }}>
                Manage your chat history
              </p>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button 
                  className="threatcalidraw__export-chat-button"
                  onClick={exportChatHistory}
                  style={{
                    backgroundColor: "#f59e0b",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "0.25rem",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#d97706")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f59e0b")}
                >
                  Export Chat
                </button>
                <button 
                  className="threatcalidraw__delete-chat-button"
                  onClick={deleteChatHistory}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "0.25rem",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#c82333")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#dc3545")}
                >
                  Delete Chat
                </button>
              </div>
            </div>
            </div>
            <div className="threatcalidraw__footer">
              <FilledButton 
                label="Save Settings" 
                onClick={saveSettings}
              />
            </div>
          </div>
        );
      case "about":
        return (
          <div className="threatcalidraw__page threatcalidraw__page--about">
            <h3>About threatCalidraw</h3>
            <div className="threatcalidraw__about-content">
              <p>
                threatCalidraw is an AI-powered security analysis tool integrated
                directly into Excalidraw.
              </p>
              <p>
                It uses large language models to analyze your diagrams and
                identify potential security threats, vulnerabilities, and
                mitigations.
              </p>
              <h4>How to use:</h4>
              <ol>
                <li>Draw your system architecture or data flow diagram</li>
                <li>
                  Open the "Analyze" tab and ask questions about security risks
                </li>
                <li>
                  Use "Only Selected" to focus the analysis on specific parts of
                  your diagram
                </li>
              </ol>
              <h3>Author</h3>
              <p>
                <strong>Mohanraj Ravichandran</strong>
                <br />
                <p>
                  threatcalidraw was the result of me searching for free platforms that allowed 
                  users to bring their API keys and interact with their DFDs for threat modeling. 
                  
                  This is a passion project and I hope it helps security professionals and enthusiasts in their journey to build secure systems.
                </p>
                <div className="threatcalidraw__author-links">
                  <a
                    href="mailto:hello@binarysouljour.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Email"
                    className="threatcalidraw__author-link"
                  >
                    {EmailIcon}
                  </a>
                  <a
                    href="https://www.linkedin.com/in/binarysouljour"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                    className="threatcalidraw__author-link"
                  >
                    {LinkedinIcon}
                  </a>
                  <a
                    href="https://discordapp.com/users/701086973540040724"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Discord"
                    className="threatcalidraw__author-link"
                  >
                    {DiscordIcon}
                  </a>
                  <a
                    href="https://binarysouljour.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Website"
                    className="threatcalidraw__author-link"
                  >
                    {WebsiteIcon}
                  </a>
                </div>
                <div style={{ marginTop: "20px" }}>
                  <FilledButton
                    label="Buy me a Coffee ?"
                    onClick={() => window.open("https://buymeacoffee.com/binarysouljour", "_blank")}
                    style={{ backgroundColor: "#10b981" }}
                  />
                </div>
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      onCloseRequest={onClose}
      title="threatCalidraw - AI Powered Threat Modeling"
      showCloseButton={true}
      size="wide"
      className="threatcalidraw"
    >
      <div className="threatcalidraw__container">
        {renderSidebar()}
        <div className="threatcalidraw__content">{renderContent()}</div>
      </div>
    </Dialog>
  );
};
